import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import {
  ApiClientOptions,
  ApiClientConstructorOptions,
  ApiResponse,
  ApiError,
} from './types';
import {
  HTTP_UNAUTHORIZED,
  HTTP_FORBIDDEN,
  HTTP_NOT_FOUND,
  HTTP_UNPROCESSABLE_ENTITY,
  isRetryableStatus,
} from '../../constants/httpStatus';

// 🔥 Track if we've encountered database issues (to avoid unnecessary event dispatches)
let hasDatabaseIssue = false;

export class ApiClient {
  private client: AxiosInstance;
  private options: ApiClientConstructorOptions;

  constructor(options: ApiClientConstructorOptions) {
    this.options = {
      timeout: 30000, // Increased to 30 seconds to handle cold starts
      retries: 3,
      retryDelay: 1000,
      ...options,
    };

    this.client = axios.create({
      baseURL: options.baseURL,
      timeout: this.options.timeout,
      headers: {
        'Content-Type': 'application/json',
        ...options.defaultHeaders,
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request Interceptor for Authentication
    this.client.interceptors.request.use(
      async config => {
        if (this.options.getToken) {
          try {
            // MSAL's acquireTokenSilent already handles token refresh internally
            // If the cached token is expired, it will automatically refresh it
            const token = await this.options.getToken();
            if (config.headers) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } catch (error) {
            return Promise.reject(error);
          }
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Response Interceptor for Error Handling and Date Conversion
    this.client.interceptors.response.use(
      (response: AxiosResponse): AxiosResponse => {
        const databaseStatus = response.headers['x-database-status'];

        if (databaseStatus === 'waking') {
          console.log(
            '[ApiClient] 🔥 Cold start detected - dispatching waking event'
          );
          hasDatabaseIssue = true;
          window.dispatchEvent(
            new CustomEvent('database-cold-start', {
              detail: { status: 'waking' },
            })
          );
        } else if (databaseStatus === 'ip-blocked') {
          const blockedIp = response.headers['x-blocked-ip'];
          console.log(
            '[ApiClient] 🚫 IP BLOCKED detected - dispatching event',
            { ipAddress: blockedIp }
          );
          hasDatabaseIssue = true;
          window.dispatchEvent(
            new CustomEvent('database-ip-blocked', {
              detail: { ipAddress: blockedIp },
            })
          );
        } else if (databaseStatus === 'ready') {
          // Database explicitly ready - clear modals
          console.log('[ApiClient] ✅ Database ready - clearing modals');
          hasDatabaseIssue = false;
          window.dispatchEvent(
            new CustomEvent('database-cold-start', {
              detail: { status: 'ready' },
            })
          );
          window.dispatchEvent(
            new CustomEvent('database-ip-blocked', {
              detail: { ipAddress: null },
            })
          );
        } else if (hasDatabaseIssue) {
          // 🔥 First successful response after a problem - clear modals
          console.log(
            '[ApiClient] ✅ First successful response after issue - clearing modals'
          );
          hasDatabaseIssue = false;
          window.dispatchEvent(
            new CustomEvent('database-cold-start', {
              detail: { status: 'ready' },
            })
          );
          window.dispatchEvent(
            new CustomEvent('database-ip-blocked', {
              detail: { ipAddress: null },
            })
          );
        }
        // Normal responses without prior issues don't emit events

        // Transform date strings to Date objects
        response.data = this.transformDates(response.data);
        return response;
      },
      async (error: AxiosError) => {
        // 🔥 Check for IP blocked header on error responses
        if (error.response?.headers) {
          const databaseStatus = error.response.headers['x-database-status'];
          const blockedIp = error.response.headers['x-blocked-ip'];

          if (databaseStatus === 'ip-blocked' && blockedIp) {
            window.dispatchEvent(
              new CustomEvent('database-ip-blocked', {
                detail: { ipAddress: blockedIp },
              })
            );
          }
        }

        if (!error.response) {
          // Request was cancelled via AbortSignal — rethrow as a standard
          // DOMException so callers can distinguish intentional cancellation
          // from genuine network failures. Do NOT wrap as NETWORK_ERROR.
          if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
            return Promise.reject(
              new DOMException('Request cancelled', 'AbortError')
            );
          }

          // Network or connection error (timeout, no internet, etc.)
          const url = error.config?.url || 'unknown endpoint';
          const timeout = this.options.timeout || 10000;
          const apiError: ApiError = new Error(
            `Unable to connect to ${url}. The request timed out after ${timeout}ms. Please check your connection and try again.`
          ) as ApiError;
          apiError.code = 'NETWORK_ERROR';
          return Promise.reject(apiError);
        }

        // Handle specific HTTP errors
        switch (error.response.status) {
          case HTTP_UNAUTHORIZED:
            if (this.options.onAuthError) {
              this.options.onAuthError();
            }
            return Promise.reject(
              this.createApiError(
                'Unauthorized',
                'UNAUTHORIZED',
                error.response
              )
            );
          case HTTP_FORBIDDEN:
            return Promise.reject(
              this.createApiError('Forbidden', 'FORBIDDEN', error.response)
            );
          case HTTP_NOT_FOUND:
            return Promise.reject(
              this.createApiError(
                'Resource not found',
                'NOT_FOUND',
                error.response
              )
            );
          case HTTP_UNPROCESSABLE_ENTITY:
            return Promise.reject(
              this.createApiError(
                'Validation Error',
                'VALIDATION_ERROR',
                error.response
              )
            );
          default:
            return Promise.reject(
              this.createApiError(
                'An unexpected error occurred',
                'UNKNOWN_ERROR',
                error.response
              )
            );
        }
      }
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private transformDates(data: any): any {
    if (!data || typeof data !== 'object') return data;

    // Handle arrays
    if (Array.isArray(data)) {
      return data.map(item => this.transformDates(item));
    }

    // Handle objects
    const transformed = { ...data };
    const datePattern = /_date$/;

    for (const [key, value] of Object.entries(transformed)) {
      if (typeof value === 'string' && datePattern.test(key)) {
        // Convert ISO 8601 date strings to Date objects
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          transformed[key] = date;
        }
      } else if (value && typeof value === 'object') {
        // Recursively transform nested objects
        transformed[key] = this.transformDates(value);
      }
    }

    return transformed;
  }

  private createApiError(
    message: string,
    code: string,
    response?: ApiResponse
  ): ApiError {
    const error: ApiError = new Error(message) as ApiError;
    error.code = code;
    error.response = response;
    return error;
  }

  /**
   * Determines if an HTTP status code warrants a retry
   * Uses the RETRYABLE_STATUS_CODES constant from httpStatus.ts
   */
  private isRetryableStatusCode(status?: number): boolean {
    if (!status) return false;
    return isRetryableStatus(status);
  }

  /**
   * Determines if an error is retryable (network errors or specific HTTP statuses)
   */
  private isRetryableError(error: AxiosError): boolean {
    // Cancelled requests (AbortSignal) must never be retried.
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      return false;
    }

    // Network errors (no response) are always retryable
    if (!error.response) {
      return true;
    }

    // Check if HTTP status is retryable
    return this.isRetryableStatusCode(error.response.status);
  }

  /**
   * Calculates exponential backoff delay with jitter
   * @param attempt - Current attempt number (0-indexed)
   * @param baseDelay - Base delay in milliseconds
   * @returns Delay in milliseconds
   */
  private calculateBackoffDelay(attempt: number, baseDelay: number): number {
    // Exponential backoff: baseDelay * 2^attempt
    const exponentialDelay = baseDelay * Math.pow(2, attempt);

    // Add jitter (random variation ±25%) to prevent thundering herd
    const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);

    // Cap at 30 seconds
    return Math.min(exponentialDelay + jitter, 30000);
  }

  /**
   * Executes a request with retry logic and exponential backoff
   */
  private async executeWithRetry<T>(
    requestFn: () => Promise<ApiResponse<T>>,
    url: string,
    attempt: number = 0
  ): Promise<ApiResponse<T>> {
    try {
      return await requestFn();
    } catch (error) {
      const axiosError = error as AxiosError;
      const maxRetries = this.options.retries || 3;
      const baseDelay = this.options.retryDelay || 1000;

      // Check if we should retry
      const shouldRetry =
        attempt < maxRetries && this.isRetryableError(axiosError);

      if (!shouldRetry) {
        // No more retries or non-retryable error, throw it
        throw error;
      }

      // Calculate backoff delay
      const delay = this.calculateBackoffDelay(attempt, baseDelay);
      const statusCode = axiosError.response?.status || 'network error';

      console.warn(
        `[ApiClient] Request to ${url} failed with ${statusCode}. ` +
          `Retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${maxRetries})...`
      );

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));

      // Retry the request
      return this.executeWithRetry(requestFn, url, attempt + 1);
    }
  }

  // Generic request methods with retry logic
  async get<T = unknown>(
    url: string,
    config?: ApiClientOptions
  ): Promise<ApiResponse<T>> {
    return this.executeWithRetry(() => this.client.get(url, config), url);
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: ApiClientOptions
  ): Promise<ApiResponse<T>> {
    return this.executeWithRetry(
      () => this.client.post(url, data, config),
      url
    );
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: ApiClientOptions
  ): Promise<ApiResponse<T>> {
    return this.executeWithRetry(() => this.client.put(url, data, config), url);
  }

  async delete<T = void>(
    url: string,
    config?: ApiClientOptions
  ): Promise<ApiResponse<T>> {
    return this.executeWithRetry(() => this.client.delete(url, config), url);
  }
}

// Create and export the default API client instance
export const api = new ApiClient({
  baseURL: process.env.REACT_APP_API_BASE || '/api',
});

export default api;
